import React from "react";
import { Helmet } from "react-helmet";
import NavBar from "../Components/NavBar/NavBar";
import { Container, Row, Col, Button } from "react-bootstrap";
import Footer from "../Components/Footer/Footer";

const hero = [
  {
    title: 'Material‑Grade Simplicity',
    desc: 'Minimal UI, maximum clarity—manage plans with just a few taps.',
    icon: 'bi-check-circle-fill'
  },
  {
    title: 'Smart Integrations',
    desc: 'Approved transactions reflect instantly in your DBS app.',
    icon: 'bi-lightning-charge-fill'
  },
  {
    title: 'Secure & Personalized',
    desc: 'Tailored EMI plans, securely routed via DBS eGIRO protocols.',
    icon: 'bi-shield-lock-fill'
  },
]

const metricCards = [
  {
    icon: 'bi-graph-up-arrow',
    value: '+32%',
    label: 'Adoption Growth',
    statIcon: 'bi-arrow-up-right'
  },
  {
    icon: 'bi-people-fill',
    value: '85%',
    label: 'User Satisfaction',
    statIcon: 'bi-check-circle'
  },
  {
    icon: 'bi-lightning-charge',
    value: '2.1s',
    label: 'Avg Processing',
    statIcon: 'bi-speedometer2'
  },
  {
    icon: 'bi-shield-check',
    value: '99.9%',
    label: 'Success Rate',
    statIcon: 'bi-shield-lock'
  },
  {
    icon: 'bi-currency-exchange',
    value: '1.2M',
    label: 'Transactions',
    statIcon: 'bi-cash-stack'
  },
  {
    icon: 'bi-globe2',
    value: '18',
    label: 'Countries',
    statIcon: 'bi-geo-alt'
  }
];

const colorSchemes = [
  { bg: 'bg-success-soft', text: 'text-success', iconBg: 'bg-success' },
  { bg: 'bg-primary-soft', text: 'text-primary', iconBg: 'bg-primary' },
  { bg: 'bg-warning-soft', text: 'text-warning', iconBg: 'bg-warning' },
  { bg: 'bg-info-soft', text: 'text-info', iconBg: 'bg-info' },
  { bg: 'bg-purple-soft', text: 'text-purple', iconBg: 'bg-purple' },
  { bg: 'bg-teal-soft', text: 'text-teal', iconBg: 'bg-teal' }
];

const eligibilityData = {
  personal: {
    icon: 'bi-person-arms-up',
    title: 'Personal Account',
    criteria: [
      'Singapore resident (21+ years)',
      'DBS/POSB account (6+ months active)',
      'Monthly income ≥ $2,500',
      'Credit score AA or better',
      'No defaults in 12 months'
    ],
    badge: 'Most Popular'
  },
  business: {
    icon: 'bi-building-gear',
    title: 'Business Account',
    criteria: [
      'Registered Singapore entity',
      'Corporate account (3+ months)',
      'Monthly turnover ≥ $10k',
      'Valid UEN/ACRA registration',
      'No payment disputes'
    ],
    badge: 'Startup Friendly'
  }
};

const benefits = [
  { icon: 'bi-rocket', title: 'Instant Setup', text: 'Ready in 90 seconds' },
  { icon: 'bi-currency-exchange', title: '0% Fees', text: 'First 6 months free' },
  { icon: 'bi-shield-lock', title: 'Bank-Grade', text: '256-bit encryption' }
];

const steps = [
  {
    step: "01",
    title: "Digital Verification",
    description: "Secure login via DBS iBanking or mBanking",
    icon: "bi-fingerprint",
    color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
  },
  {
    step: "02",
    title: "Merchant Connect",
    description: "Select from 500+ integrated partners",
    icon: "bi-handbag",
    color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
  },
  {
    step: "03",
    title: "Smart Configuration",
    description: "AI-powered payment scheduling",
    icon: "bi-sliders2-vertical",
    color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
  },
  {
    step: "04",
    title: "Instant Activation",
    description: "Real-time approval with e-Signature",
    icon: "bi-lightning-charge",
    color: "linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)"
  }
];

const EmiWithDbs = () => {

  return (
    <div>
      <Helmet></Helmet>
      <NavBar />
      <>
        <section className="py-5 mt-lg-5 mb-lg-5 bg-white position-relative overflow-hidden">
          <Container>
            <Row className="align-items-center g-4 g-lg-5">
              <Col lg={6} className="position-relative z-1">
                <h2 className="display-4 fw-bold text-dark mb-4 text-center text-lg-start">
                  Welcome to <span className="text-primary-gradient">DBS eGIRO</span> EMI
                </h2>
                {/* <p className="lead text-muted mb-4 mb-lg-5">
                  DBS eGIRO is a smarter way to set up recurring Installments directly from your bank account—no cards, no paperwork, just seamless EMI routing.
                </p> */}

                <div className="d-flex flex-column gap-3 mb-4 mb-lg-5">
                  {hero.map((item, idx) => (
                    <div key={idx} className="d-flex align-items-start">
                      <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                        <i className={`bi ${item.icon} text-primary fs-4`} />
                      </div>
                      <div>
                        <h5 className="fw-semibold mb-1">{item.title}</h5>
                        <p className="text-muted mb-0">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex flex-wrap justify-content-center justify-content-lg-start gap-3 py-2 py-lg-0">
                  <Button
                    variant="primary"
                    className="rounded-pill btn-sm btn-md btn-lg btn-xl btn-xxl px-3 px-sm-4 px-md-5 py-2"
                  >
                    Learn More
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="rounded-pill btn-sm btn-md btn-lg btn-xl btn-xxl px-3 px-sm-4 px-md-5 py-2"
                  >
                    How It Works
                  </Button>
                </div>
              </Col>
              <Col lg={6} className="d-flex">
                <div className="bg-light bg-opacity-10 rounded-4 p-3 p-md-2 w-100 h-100">
                  <div className="d-flex flex-wrap gap-3 justify-content-center">
                    {metricCards.map((card, index) => {
                      const colors = colorSchemes[index % colorSchemes.length];
                      return (
                        <div
                          key={index}
                          className={`${colors.bg} rounded-3 p-3 d-flex flex-column align-items-center`}
                        >
                          <div className={`${colors.iconBg} bg-opacity-10 p-3 rounded-circle mb-2`}>
                            <i className={`bi ${card.icon} ${colors.text} fs-3`}></i>
                          </div>
                          <div className="text-center">
                            <h3 className="fw-bold mb-1">{card.value}</h3>
                            <p className="small mb-2">{card.label}</p>
                            <i className={`bi ${card.statIcon} ${colors.text} small`}></i>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="py-5 bg-white">
          <Container className="px-lg-5">
            <div className="text-center mb-5">
              <span className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2 rounded-pill fw-medium">
                <i className="bi bi-patch-check me-2"></i>
                Eligibility
              </span>
              <h2 className="fw-bold mb-3">
                Who Qualifies for <span className="text-primary">eGIRO EMI</span>
              </h2>
              <p className="text-muted mb-0 mx-auto" style={{ maxWidth: "600px" }}>
                Simple requirements for seamless recurring payments
              </p>
            </div>

            <Row className="g-4 g-lg-5 mb-5">
              {Object.entries(eligibilityData).map(([key, item]) => (
                <Col lg={6} key={key}>
                  <div className="h-100 p-4 p-lg-5 border rounded-3 bg-white shadow-sm">
                    <div className="d-flex align-items-start mb-4">
                      <div className="bg-primary bg-opacity-10 p-2 rounded-2 me-3">
                        <i className={`bi ${item.icon} text-primary fs-4`} />
                      </div>
                      <h3 className="fw-bold mb-0">{item.title}</h3>
                    </div>

                    <ul className="list-unstyled">
                      {item.criteria.map((point, i) => (
                        <li key={i} className="mb-3 d-flex">
                          <i className="bi bi-check-circle text-success me-2 mt-1"></i>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
              ))}
            </Row>

            <div className="text-center mb-5">
              <h5 className="text-dark fw-bold">AZZ Enterprises | EMI eGIRO Plan</h5>
              <p className="lead text-muted mb-0">
                Make Payments the Smart Way — Easy, Secure & Flexible!
              </p>
            </div>

            <Row className="g-4 mb-5">
              <Col md={6}>
                <div className="p-4 text-center bg-white rounded-4 border shadow-sm h-100 transition-hover">
                  <div className="mx-auto mb-3 p-3 rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-cash-stack fs-3"></i>
                  </div>
                  <h6 className="fw-bold mb-2">Processing Fees</h6>
                  <p className="text-muted small mb-0">
                    For amounts below SGD 500 → SGD 25<br />
                    For amounts SGD 500 & above → SGD 50
                  </p>
                </div>
              </Col>

              <Col md={6}>
                <div className="p-4 text-center bg-white rounded-4 border shadow-sm h-100 transition-hover">
                  <div className="mx-auto mb-3 p-3 rounded-circle bg-warning text-white d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-clock-history fs-3"></i>
                  </div>
                  <h6 className="fw-bold mb-2">Hassle-Free Payments</h6>
                  <p className="text-muted small mb-0">
                    Enjoy hassle-free monthly payments with eGIRO Auto-Debit<br />
                    ⚡ Fast approval | 🔒 Secure setup | 💼 Simple process
                  </p>
                </div>
              </Col>
            </Row>



            {/* <div className="text-center">
              <Button
                variant="primary"
                className="rounded-pill btn-sm btn-md btn-lg btn-xl btn-xxl px-3 px-sm-4 px-md-5 py-2"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/sample-dbs-egiro-guide.pdf';
                  link.download = 'DBS-eGIRO-Guide.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <span className="d-inline-flex align-items-center">
                  <i className="bi bi-file-earmark-pdf me-2"></i>
                  Download eGIRO Guide
                </span>
              </Button>
            </div> */}
          </Container>
        </section>

        <section className="py-5 mt-lg-5 bg-white">
          <Container>
            <Row className="justify-content-center mb-4 mb-md-5">
              <Col xs={12} lg={10} xl={8} className="text-center">
                <span className="badge bg-danger bg-opacity-10 text-danger mb-3 px-3 py-2 rounded-pill fw-medium d-inline-flex align-items-center">
                  <i className="bi bi-lightning-charge-fill me-2"></i>
                  Quick Process
                </span>
                <h2 className="display-6 fw-bold mb-3">
                  <span className="text-gradient-red">4-Step</span> Instant Setup
                </h2>
                <p className="lead text-muted px-lg-4 mb-0">
                  Activate in minutes with our streamlined flow
                </p>
              </Col>
            </Row>

            <Row className="g-3 g-md-4">
              {steps.map((item, index) => (
                <Col xs={12} sm={6} lg={3} key={index}>
                  <div className="h-100 p-4 rounded-4 shadow-sm border-0 bg-white position-relative overflow-hidden transition-all hover-lift">
                    <div
                      className="position-absolute top-0 start-0 w-100 h-4"
                      style={{ background: item.color }}
                    ></div>
                    <div className="position-relative z-1 pt-2">
                      <span className="display-6 fw-bold text-danger opacity-10 mb-0">{item.step}</span>
                      <div className="d-flex align-items-start mb-3">
                        <div
                          className="p-3 rounded-3 me-3 flex-shrink-0"
                          style={{
                            background: `${item.color}10`,
                            border: `1px solid ${item.color}20`
                          }}
                        >
                          <i className={`bi ${item.icon} fs-3 text-danger`}></i>
                        </div>
                        <h3 className="h5 fw-bold mt-1 mb-2">{item.title}</h3>
                      </div>
                      <p className="text-muted mb-0">{item.description}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            <div className="text-center mt-5 mt-md-6">
              <Button
                variant="danger"
                className="rounded-pill btn-sm btn-md btn-lg btn-xl btn-xxl px-3 px-sm-4 px-md-5 py-2 me-3"
              >
                <i className="bi bi-play-fill me-2"></i>
                Watch Demo
              </Button>
              <a
                href="https://www.dbs.com.sg/personal/cards/features/pay-later"
                className="btn btn-outline-danger rounded-pill btn-sm btn-md btn-lg btn-xl btn-xxl px-3 px-sm-4 px-md-5 py-2d-inline-flex align-items-centertext-decoration-none"
              >
                Start Now <i className="bi bi-arrow-right ms-2"></i>
              </a>
            </div>
          </Container>
        </section>
      </>
      <Footer />
    </div>
  );
};

export default EmiWithDbs;
